import { useState } from "react";
import { ChevronDown, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/context/ToastContext";
import { downloadFile } from "@/lib/api";

export type ExportFormat = "pdf" | "xlsx";

/**
 * Export ▾ — a PDF and an Excel file built on the server from the report's current filters.
 * Each option shows progress and says plainly whether it worked.
 */
export function ExportMenu({
  fileUrl,
  post,
  activeFilters = 0,
  label,
  disabled,
}: {
  /** URL for a PDF or Excel file of this report with its current filters; leave out when the report has none */
  fileUrl?: (format: ExportFormat) => string;
  /** Or: a POST that returns the file (Ask AI reports send their search as the body) */
  post?: { url: string; body: (format: ExportFormat) => unknown };
  /** Shown in the menu so people know the file follows the filters */
  activeFilters?: number;
  /** The menu's first line, instead of the period and filter count */
  label?: string;
  disabled?: boolean;
}) {
  const { success, error } = useToast();
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  if (!fileUrl && !post) return null;

  const run = async (kind: ExportFormat) => {
    setBusy(kind);
    try {
      const name = post ? await downloadFile(post.url, `report.${kind}`, post.body(kind)) : await downloadFile(fileUrl!(kind), `report.${kind}`);
      success(kind === "pdf" ? "PDF downloaded" : "Excel file downloaded", name);
    } catch (err: any) {
      error("Couldn't download the report", err?.message || "Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={!!busy || disabled} aria-label="Export report">
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          {busy ? `Preparing ${busy === "pdf" ? "PDF" : "Excel"}…` : "Export"}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {label || (activeFilters ? `Uses the period and ${activeFilters} filter${activeFilters === 1 ? "" : "s"} shown` : "Uses the period shown")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void run("pdf")} className="gap-3 py-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-danger/10 text-danger">
            <FileText className="size-4" />
          </span>
          <span className="flex flex-col">
            <span className="font-medium">PDF report</span>
            <span className="text-[11px] text-muted-foreground">Coloured summary, charts and tables</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void run("xlsx")} className="gap-3 py-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-success/10 text-success">
            <FileSpreadsheet className="size-4" />
          </span>
          <span className="flex flex-col">
            <span className="font-medium">Excel (.xlsx)</span>
            <span className="text-[11px] text-muted-foreground">Every row, one coloured sheet per section</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
